import { CeilingPlane, RoofPlane } from '@paragon/contract-library';
import { Line2D, LineSegment2D } from '@paragon/geometry-library';
import { PartialPartial } from '@paragon/utilities';

/** The subset of RoofPlane properties that can be edited. */
export type EditableRoofPlaneProperties = PartialPartial<Omit<RoofPlane, 'guid'>, 'geometry'>;

/** A patch for roof planes generated by operations in the properties or insertion panel. */
export type EditedRoofPlaneProperties = Partial<Omit<RoofPlane, 'guid'>>;

/** The subset of CeilingPlane properties that can be edited. */
export type EditableCeilingPlaneProperties = PartialPartial<Omit<CeilingPlane, 'guid'>, 'geometry'>;

/** A patch for ceiling planes generated by operations in the properties or insertion panel. */
export type EditedCeilingPlaneProperties = Partial<Omit<CeilingPlane, 'guid'>>;

/** A patch for planes generated by operations in the properties or insertion panel. */
export type EditedPlaneProperties = EditedRoofPlaneProperties | EditedCeilingPlaneProperties;

export interface RoofPlaneEditInstructions {
  guid: string;
  editedProperties: EditedRoofPlaneProperties;
}

export interface CeilingPlaneEditInstructions {
  guid: string;
  editedProperties: EditedCeilingPlaneProperties;
}

export type PlaneEditInstructions<T extends RoofOrCeilingPlane> = T extends RoofPlane
  ? RoofPlaneEditInstructions
  : T extends CeilingPlane
    ? CeilingPlaneEditInstructions
    : never;

// If you update this definition, make sure to update `isInsertionRoofPlaneFullyDefined` as well.
export type InsertionRoofPlane = RoofPlane | NonGeometricInsertionRoofPlane;

// If you update this definition, make sure to update `isInsertionCeilingPlaneFullyDefined` as well.
export type InsertionCeilingPlane = CeilingPlane | NonGeometricInsertionCeilingPlane;

export type NonGeometricInsertionRoofPlane = PartialPartial<RoofPlane, 'geometry'>;

export type NonGeometricInsertionCeilingPlane = PartialPartial<CeilingPlane, 'geometry'>;

export type RoofOrCeilingPlane = RoofPlane | CeilingPlane;

export function isRoofPlane(plane: RoofOrCeilingPlane): plane is RoofPlane;
export function isRoofPlane<TPlane extends RoofOrCeilingPlane>(plane: TPlane): plane is TPlane & RoofPlane;
export function isRoofPlane(plane: InsertionRoofPlane | InsertionCeilingPlane): plane is InsertionRoofPlane;
export function isRoofPlane(plane: DisplayablePlane): plane is DisplayablePlane<RoofPlane>;
export function isRoofPlane(
  plane: DisplayablePlaneWithCalculatedGeometry,
): plane is DisplayablePlaneWithCalculatedGeometry<RoofPlane>;
export function isRoofPlane(
  plane:
    | RoofOrCeilingPlane
    | InsertionRoofPlane
    | InsertionCeilingPlane
    | DisplayablePlane
    | DisplayablePlaneWithCalculatedGeometry,
): boolean {
  // Type-safe; will fail compilation if interfaces change
  const ROOF_PLANE_EXCLUSIVE_KEY: Exclude<keyof RoofPlane, keyof CeilingPlane> = 'heelSettings';

  return ROOF_PLANE_EXCLUSIVE_KEY in plane;
}

/**
 * Represents a plane with an optional guid. Sometimes we display "fake" transient planes to represent vertical cuts.
 */
export type DisplayablePlane<T extends RoofOrCeilingPlane = RoofOrCeilingPlane> = T | Omit<T, 'guid'>;

/**
 * Represents a plane with an optional guid. Sometimes we display "fake" transient planes to represent vertical cuts.
 */
export type DisplayablePlaneWithCalculatedGeometry<T extends RoofOrCeilingPlane = RoofOrCeilingPlane> =
  PlaneWithCalculatedGeometry<T> | Omit<PlaneWithCalculatedGeometry<T>, 'guid'>;

export interface CalculatedRoofPlaneGeometry {
  line: Line2D;
  elevation: number;

  segment?: LineSegment2D;
}

export type PlaneWithCalculatedGeometry<T extends RoofOrCeilingPlane = RoofOrCeilingPlane> = T & {
  calculatedGeometry: CalculatedRoofPlaneGeometry;
};
